import type { Experience } from '@/hooks/use-profile'
import { formatProfileEvidenceDateRange } from '@/lib/profile-evidence-display'
import { Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface ExperienceEvidenceCardProps {
  experience: Experience
  variant: 'compact' | 'full'
  className?: string
  /** Compact only: removes this role as evidence for the current skill (e.g. unlink skill from experience). */
  onRemoveFromEvidence?: () => void
  removeBusy?: boolean
  removeAriaLabel?: string
}

/**
 * Read-only summary of a work experience for profile / skill evidence lists.
 * `compact` (skill page): title + company left, date range right; optional remove control after the date (visible on touch; hover-reveal from `sm` up).
 * `full` (profile): stacked title/company then date range; adds description when present.
 */
export function ExperienceEvidenceCard({
  experience,
  variant,
  className,
  onRemoveFromEvidence,
  removeBusy,
  removeAriaLabel,
}: ExperienceEvidenceCardProps) {
  const dateLabel = formatProfileEvidenceDateRange(experience.start_date, experience.end_date)

  if (variant === 'compact') {
    return (
      <div className={cn('group/item min-w-0', className)}>
        <div className="flex items-center justify-between gap-3">
          <p className="min-w-0 flex-1 font-medium leading-snug">
            {experience.title}{' '}
            <span className="text-muted-foreground font-normal">@ {experience.company}</span>
          </p>
          <div className="flex shrink-0 items-center gap-2">
            <p className="text-right text-xs text-muted-foreground leading-snug">{dateLabel}</p>
            {onRemoveFromEvidence ? (
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                className={cn(
                  'shrink-0 text-muted-foreground hover:text-destructive',
                  'opacity-100 transition-opacity',
                  'sm:opacity-0 sm:group-hover/item:opacity-100 focus-visible:opacity-100'
                )}
                disabled={removeBusy}
                onClick={(event) => {
                  event.stopPropagation()
                  onRemoveFromEvidence()
                }}
                aria-label={removeAriaLabel ?? 'Remove this role as evidence for this skill'}
                title={removeAriaLabel ?? 'Remove from skill evidence'}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('space-y-1 min-w-0', className)}>
      <p className="font-medium">
        {experience.title}{' '}
        <span className="text-muted-foreground">@ {experience.company}</span>
      </p>
      <p className="text-xs text-muted-foreground">{dateLabel}</p>
      {experience.description ? (
        <p className="text-base text-muted-foreground">{experience.description}</p>
      ) : null}
    </div>
  )
}
